import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Link2, Shield, Users } from "lucide-react";
import { useCallback, useMemo } from "react";
import ReloadButton from "@/components/common/reload-button";
import { AutoPagination, DataTable } from "@/components/table";
import { useServerTable } from "@/components/table/hooks/use-server-table";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  SearchBar,
  Typography,
} from "@/components/ui";
import useIsMobile from "@/components/ui/sidebar/hooks/use-is-mobile";
import useSwitchSuperUser from "@/features/auth/hooks/use-superuser-mutation.ts";
import AddCustomerDialog from "@/features/root/components/add-customer-dialog";
import { CustomerDropdown } from "@/features/root/components/customer-dropdown";
import { useCustomersQuery } from "@/features/root/hooks/use-customers-query";
import type { Customer } from "@/types/root/root.type";

export const Route = createFileRoute("/_protected/root/home")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { mutate: switchSuperUser } = useSwitchSuperUser();

  const handleSuperUserLogin = useCallback(
    (id: string) => {
      switchSuperUser(id);
    },
    [switchSuperUser],
  );

  const handleNavigateToCustomerRelation = useCallback(
    (id: string) => {
      navigate({
        to: "/root/customer-relation",
        search: { customerId: id },
      });
    },
    [navigate],
  );

  const handleNavigateToUsers = useCallback(
    (id: string) => {
      navigate({ to: "/root/users", search: { customerId: id } });
    },
    [navigate],
  );

  const columns: ColumnDef<Customer>[] = useMemo(
    () => [
      {
        accessorKey: "isActive",
        header: "狀態",
        cell: ({ getValue }) => {
          const isActive = getValue<boolean>();
          return (
            <Badge variant={isActive ? "default" : "secondary"}>{isActive ? "啟用" : "停用"}</Badge>
          );
        },
      },
      {
        accessorKey: "taxId",
        header: "統編",
        cell: ({ getValue }) => {
          const taxId = getValue<string>();
          return <span className="font-mono">{taxId}</span>;
        },
      },
      {
        accessorKey: "name",
        header: "公司名稱",
        cell: ({ getValue }) => {
          const name = getValue<string>();
          return <span className="font-medium">{name}</span>;
        },
      },
      {
        accessorKey: "role",
        header: "公司類型",
        cell: ({ getValue }) => {
          const role = getValue<string>();
          const isFactory = role === "factory";
          return (
            <Badge variant={isFactory ? "default" : "secondary"}>
              {isFactory ? "中心廠" : "供應商"}
            </Badge>
          );
        },
      },
      {
        id: "superUserLogin",
        header: "超級使用者登入",
        cell: ({ row }) => {
          const customer: Customer = row.original;
          return (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleSuperUserLogin(customer.id)}
              className="flex items-center gap-2"
            >
              <Shield />
              登入
            </Button>
          );
        },
      },
      {
        id: "userManagement",
        header: "使用者管理",
        cell: ({ row }) => {
          const customer: Customer = row.original;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigateToUsers(customer.id)}
              className="flex items-center gap-2"
            >
              管理使用者
              <Users />
            </Button>
          );
        },
      },
      {
        id: "customerRelation",
        header: "客戶關聯",
        cell: ({ row }) => {
          const customer: Customer = row.original;
          const isFactory = customer.role === "factory";
          if (!isFactory) return null;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleNavigateToCustomerRelation(customer.id)}
            >
              <Link2 />
              設定關聯
            </Button>
          );
        },
      },
      {
        id: "actions",
        header: "操作",
        cell: ({ row }) => {
          const customer: Customer = row.original;
          return <CustomerDropdown customer={customer} />;
        },
      },
    ],
    [handleSuperUserLogin, handleNavigateToUsers, handleNavigateToCustomerRelation],
  );

  const { table, isFetching, globalFilter, setGlobalFilter, onPaginationChange, refetch } =
    useServerTable<Customer>({
      queryFn: useCustomersQuery,
      initialPageSize: 20,
      columns,
    });

  return (
    <section>
      <Card>
        <CardHeader>
          <title>客戶管理頁面｜Supplier Link</title>
          <Typography variant="h2">客戶列表</Typography>
          <CardDescription>管理您的客戶資料，包含統編、公司名稱和帳戶資訊</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="w-full md:w-auto">
              <SearchBar
                placeholder="請輸入客戶統編或公司名稱進行搜尋..."
                className="grow"
                value={globalFilter}
                onChange={setGlobalFilter}
              />
            </span>
            <div className="flex items-center gap-2">
              <AddCustomerDialog />
              <ReloadButton onClick={refetch} />
            </div>
          </div>

          <DataTable table={table} isFetching={isFetching} />

          <AutoPagination
            table={table}
            showTotalCount
            showSizeChanger={!isMobile}
            onPaginationChange={onPaginationChange}
          />
        </CardContent>
      </Card>
    </section>
  );
}
